// Playback for a /speak topic page.
//
// ⛔ No React here. The dialogue is already rendered by the server — this is
// progressive enhancement over it, and the page has to stay complete and
// readable for a reader whose device cannot speak. The choosing lives in
// utils/speakPlayback so it is testable without a browser; this file is the
// wiring: find the elements, listen, speak, mark.

import { shouldRevealControls, assignRoleVoices, rateFor, playbackOrder } from 'utils/speakPlayback'

function setup() {
  const root = document.getElementById('speak-topic')
  if (!root) return

  // ⛔ The only gate that matters. A browser with no speech synthesis leaves
  // every control hidden and the page is still the whole dialogue.
  const synth = window.speechSynthesis
  if (!synth) return

  const lineNodes = Array.from(root.querySelectorAll('li[data-speak-line]'))
  if (lineNodes.length === 0) return

  const dialogueControls = root.querySelector('[data-speak-dialogue]')
  const lineControls = Array.from(root.querySelectorAll('[data-speak-controls]'))

  let roleVoices = { other: null, learner: null }
  let revealed = false

  // Every utterance carries the generation it was started in. `cancel()` fires
  // `onend` on the utterance it kills in some browsers, which would otherwise
  // advance the whole-dialogue sequence one line for every interruption.
  let generation = 0

  function clearMarks() {
    lineNodes.forEach((node) => node.removeAttribute('data-speaking'))
  }

  function reveal() {
    if (revealed) return
    revealed = true

    lineControls.forEach((node) => node.classList.remove('hidden'))
    if (dialogueControls) dialogueControls.classList.remove('hidden')
  }

  // iOS Safari returns an empty list on the first call and fills it in later,
  // so this runs again on every `voiceschanged`. A device that can in fact
  // speak must not be left with the controls hidden because of one early read.
  function refreshVoices() {
    const voices = synth.getVoices()

    roleVoices = assignRoleVoices(voices)
    if (shouldRevealControls(voices)) reveal()
  }

  function utteranceFor(node, speed) {
    const text = node.dataset.speakText
    if (!text) return null

    const utterance = new SpeechSynthesisUtterance(text)
    const voice = roleVoices[node.dataset.speakSpeaker] || roleVoices.other

    if (voice) {
      utterance.voice = voice
      utterance.lang = voice.lang
    } else {
      utterance.lang = 'en-US'
    }
    utterance.rate = rateFor(speed)

    return utterance
  }

  // One line, on demand. Whatever was being said stops first: two voices over
  // each other teaches nothing, and the mark would be left on two lines.
  function speakLine(node, speed) {
    const mine = ++generation

    synth.cancel()
    clearMarks()

    const utterance = utteranceFor(node, speed)
    if (!utterance) return

    const finish = () => {
      if (mine !== generation) return
      node.removeAttribute('data-speaking')
    }

    utterance.onstart = () => {
      if (mine !== generation) return
      node.setAttribute('data-speaking', 'true')
    }
    // ⛔ Both, not just onend: a voice that fails mid-sentence would otherwise
    // leave the line marked as speaking for the rest of the visit.
    utterance.onend = finish
    utterance.onerror = finish

    synth.speak(utterance)
  }

  // The whole scene, in number order, alternating the two voices. This is the
  // practice neither the Threads post nor a printed page can offer.
  function playAll() {
    const mine = ++generation

    synth.cancel()
    clearMarks()

    const queue = playbackOrder(
      lineNodes.map((node) => ({ number: Number(node.dataset.speakLine), node }))
    )

    const speakAt = (index) => {
      if (mine !== generation || index >= queue.length) {
        clearMarks()
        return
      }

      const { node } = queue[index]
      const utterance = utteranceFor(node, 'normal')
      if (!utterance) {
        speakAt(index + 1)
        return
      }

      utterance.onstart = () => {
        if (mine !== generation) return

        clearMarks()
        node.setAttribute('data-speaking', 'true')
        // The dialogue is longer than a screen, so the line being spoken has to
        // come to the reader rather than the reader hunting for it.
        node.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
      utterance.onend = () => speakAt(index + 1)
      // A line the device chokes on stops the run rather than silently
      // skipping ahead — a run that plays 9 of 12 lines looks like it worked.
      utterance.onerror = () => {
        if (mine !== generation) return
        clearMarks()
      }

      synth.speak(utterance)
    }

    speakAt(0)
  }

  function stop() {
    generation += 1
    synth.cancel()
    clearMarks()
  }

  lineNodes.forEach((node) => {
    node.querySelectorAll('button[data-speak-play]').forEach((button) => {
      button.addEventListener('click', () => speakLine(node, button.dataset.speakPlay))
    })
  })

  if (dialogueControls) {
    const playAllButton = dialogueControls.querySelector('button[data-speak-play-all]')
    const stopButton = dialogueControls.querySelector('button[data-speak-stop]')

    if (playAllButton) playAllButton.addEventListener('click', playAll)
    if (stopButton) stopButton.addEventListener('click', stop)
  }

  // Leaving the page mid-sentence otherwise keeps the voice going in Chrome.
  window.addEventListener('pagehide', () => synth.cancel())

  refreshVoices()
  synth.addEventListener?.('voiceschanged', refreshVoices)
}

document.addEventListener('DOMContentLoaded', setup)
